/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import PrimaryButtonContentful from 'bundles/coursera-plus-landing-page/components/ctaContentful/PrimaryButtonContentful';
import { CTA_LOCATIONS, PRESENTATIONS } from 'bundles/coursera-plus-landing-page/contentful/constants';
import CollectionProductCard from 'bundles/coursera-plus-landing-page/courseraPlusCollections/CollectionProductCard';
import { BUNDLE_MAP, useFetchBundleProducts } from 'bundles/coursera-plus/utils/bundleUtils';

import _t from 'i18n!nls/coursera-plus-landing-page';

const useStyles = () => {
  return {
    wrapper: css`
      width: 100%;
      padding-bottom: var(--cds-spacing-400);
      padding-top: var(--cds-spacing-400);
    `,
    container: css`
      max-width: 1200px;
      padding: 20px;
      margin: 0 auto;
    `,
    sectionContent: css`
      justify-content: space-between;

      @media (max-width: 788px) {
        flex-direction: column;
      }
    `,
    valuePropsWrapper: css`
      ${breakpoints.down('sm')} {
        margin-top: var(--cds-spacing-400);
      }
    `,
    valueProps: css`
      margin: 0;
      padding: 0;
      margin-top: var(--cds-spacing-200);
      list-style-type: none;

      li {
        display: flex;
        gap: 10px;
        align-items: center;
        margin-bottom: var(--cds-spacing-100);
      }
    `,
    sectionItem: css`
      @media (max-width: 788px) {
        max-width: 100%;
      }
    `,
    ctaWrapper: css`
      margin-top: var(--cds-spacing-300);
      margin-left: var(--cds-spacing-100);
    `,
    title: css`
      margin-bottom: var(--cds-spacing-400);
    `,
  };
};

type Prop = {
  slug?: string | null;
  ownsCourseraPlus: boolean;
  ownsCourseraLite: boolean;
};

const CollectionProductListContainer = ({ slug, ownsCourseraLite, ownsCourseraPlus }: Prop) => {
  const styles = useStyles();
  const products = useFetchBundleProducts(slug as string);

  const shouldShowCTA = ownsCourseraLite || ownsCourseraPlus;
  const valuePropsList = [
    _t('Self-paced learning'),
    _t('Video lectures'),
    _t('Graded assignments'),
    _t('Practice quizzes'),
    _t('Certificates of completion'),
    _t('Try courses and switch between them'),
  ];

  if (!slug) {
    return null;
  }

  return (
    <div css={styles.wrapper}>
      <div css={styles.container}>
        <Typography variant="d2semibold" css={styles.title}>
          {BUNDLE_MAP[slug].name}
        </Typography>
        <Grid container direction="row" css={styles.sectionContent}>
          <Grid item sm={8} xs={12} css={styles.sectionItem}>
            {products?.map((product, index) => (
              <CollectionProductCard product={product} index={index} />
            ))}
          </Grid>

          <Grid item sm={3} xs={12} css={[styles.sectionItem, styles.valuePropsWrapper]}>
            <Typography2 component="h3" variant="subtitleMedium">
              {_t('This collection includes')}
            </Typography2>
            <ul css={styles.valueProps}>
              {valuePropsList.map((valueProp) => (
                <li key={valueProp}>
                  <CheckIcon size="small" color="interactive" />
                  <Typography2 component="p">{valueProp}</Typography2>
                </li>
              ))}
              {!shouldShowCTA && (
                <div css={styles.ctaWrapper} data-testid="coursera-pus-primary-cta">
                  <PrimaryButtonContentful
                    ctaLocation={CTA_LOCATIONS.sticky}
                    presentation={PRESENTATIONS.smallButton}
                    isFirstCheckoutInHero={false}
                  />
                </div>
              )}
            </ul>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default CollectionProductListContainer;
